import React, {FC} from 'react';
import {styled} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelExternalTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelBehaviorsModel';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {
  ChannelIcon,
  ChannelIconSizesEnum,
} from '../../../../../libs/shared/ui/src/components/icon/channelIcon';
import ChannelLines from '../../../../assets/images/basicsGuide/channelLines.svg';
import InboxFlourish from '../../../../assets/images/basicsGuide/inboxFlourish.svg';
import {
  CardText,
  ChannelCardIconPlaceHolder,
  StyledChannelCard,
  StyledInboxCard,
  StyledInboxIcon,
  StyledVisualContainer,
  StyledVisualDetailsText,
  StyledVisualLayout,
} from './onboardingBasicsGuideHelpers';

/*
 * Props.
 */

interface OnboardingBasicsGuideInboxVisualProps {}

/*
 * Style.
 */

const StyledChannelCardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 16px;
`;

const StyledChannelLines = styled.img`
  margin-top: 8px;
  margin-bottom: 8px;
`;

const StyledChannelIcon = styled(ChannelIcon)`
  position: absolute;
  z-index: 1;
`;

const StyledGmailChannelIcon = styled(StyledChannelIcon)`
  animation-name: inboxVisualMoveGmailIcon;
  animation-delay: 0;
  animation-duration: 400ms;
  animation-fill-mode: forwards;

  top: 341px;
  left: 146px;
  transform: scale(${68 / 40});

  @keyframes inboxVisualMoveGmailIcon {
    0% {
      top: 341px;
      left: 146px;
      transform: scale(${68 / 40});
    }
    100% {
      top: 264px;
      left: 87px;
      transform: scale(${40 / 40});
    }
  }
`;

const StyledFacebookChannelIcon = styled(StyledChannelIcon)`
  animation-name: inboxVisualMoveFacebookIcon;
  animation-delay: 0;
  animation-duration: 400ms;
  animation-fill-mode: forwards;

  top: 255.5px;
  left: 201.5px;
  transform: scale(${57 / 40});

  @keyframes inboxVisualMoveFacebookIcon {
    0% {
      top: 255.5px;
      left: 201.5px;
      transform: scale(${57 / 40});
    }
    100% {
      top: 264px;
      left: 251px;
      transform: scale(${40 / 40});
    }
  }
`;

const StyledInstagramChannelIcon = styled(StyledChannelIcon)`
  animation-name: inboxVisualMoveInstagramIcon;
  animation-delay: 0;
  animation-duration: 400ms;
  animation-fill-mode: forwards;

  top: 348.5px;
  left: 305.5px;
  transform: scale(${45 / 40});

  @keyframes inboxVisualMoveInstagramIcon {
    0% {
      top: 348.5px;
      left: 305.5px;
      transform: scale(${45 / 40});
    }
    100% {
      top: 264px;
      left: 415px;
      transform: scale(${40 / 40});
    }
  }
`;

const StyledInboxVisualContainer = styled.div`
  position: absolute;
  top: 247px;
  left: 33px;

  display: flex;
  align-items: center;
  flex-direction: column;

  ${computeFadeInAnimation('inbox-visual', 600, 400)}
`;

const StyledEmphasizedInboxCard = styled(StyledInboxCard)`
  animation-name: inboxEmphasis;
  animation-delay: 600ms;
  animation-duration: 400ms;
  animation-fill-mode: forwards;
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
  @keyframes inboxEmphasis {
    0% {
      filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
    }
    100% {
      filter: drop-shadow(0px 0px 10px rgba(77, 237, 255, 1));
    }
  }
`;

const StyledInboxFlourish = styled.img`
  position: absolute;
  top: 438px;
  left: 346px;
  z-index: 1;

  ${computeFadeInAnimation('inbox-flourish', 600, 400)}
`;

const StyledInboxVisualDetailsText = styled(StyledVisualDetailsText)`
  animation-delay: 600ms;
  animation-duration: 400ms;
`;

/*
 * Component.
 */

export const OnboardingBasicsGuideInboxVisual: FC<OnboardingBasicsGuideInboxVisualProps> = () => (
  <StyledVisualContainer>
    <StyledGmailChannelIcon type={ChannelTypesEnum.GMAIL} size={ChannelIconSizesEnum.BIG} />
    <StyledFacebookChannelIcon type={ChannelTypesEnum.FACEBOOK} size={ChannelIconSizesEnum.BIG} />
    <StyledInstagramChannelIcon type={ChannelExternalTypesEnum.INSTAGRAM} size={ChannelIconSizesEnum.BIG} />
    <StyledInboxFlourish src={InboxFlourish} alt="Your Front inbox!" />
    <StyledInboxVisualContainer>
      <StyledChannelCardContainer>
        <StyledChannelCard>
          <ChannelCardIconPlaceHolder />
          <CardText>info@acme.com</CardText>
        </StyledChannelCard>
        <StyledChannelCard>
          <ChannelCardIconPlaceHolder />
          <CardText>Acme</CardText>
        </StyledChannelCard>
        <StyledChannelCard>
          <ChannelCardIconPlaceHolder />
          <CardText>@acme</CardText>
        </StyledChannelCard>
      </StyledChannelCardContainer>
      <StyledChannelLines src={ChannelLines} alt="Channels feed inboxes" />
      <StyledEmphasizedInboxCard>
        <StyledInboxIcon name="inboxFilled" />
        <CardText>Support</CardText>
      </StyledEmphasizedInboxCard>
    </StyledInboxVisualContainer>
    <StyledVisualLayout>
      <StyledInboxVisualDetailsText>
        <Trans id="onboarding-basics-guide-inbox-visual-details">
          Front’s inboxes are where your team will go to find customer messages from one or more channels. In
          the example below, messages from Acme’s Gmail, Facebook page and Instagram account will all show up
          in the Support inbox.
        </Trans>
      </StyledInboxVisualDetailsText>
    </StyledVisualLayout>
  </StyledVisualContainer>
);

function computeFadeInAnimation(slug: string, delay: number, duration: number) {
  const animationName = `fadeIn-${slug}`;
  return `
    animation-name: ${animationName};
    animation-delay: ${delay}ms;
    animation-duration: ${duration}ms;
    animation-fill-mode: forwards;
    opacity: 0;

    @keyframes ${animationName} {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
  `;
}
